// Customer product search — client-side matcher (Sep 2026).
//
// Mirrors the backend ranker in `backend/retail/product_search.py`
// field-for-field and weight-for-weight, so a customer gets the SAME
// ordering whether the catalog was small enough to filter in the
// browser or large enough to be searched on the server. Any change here
// must be mirrored there (and is covered by tests on both sides).
//
// Weights: name x3, brand x3 (level with name — reaching a product by
// typing its brand is the point of the feature), category x2, tags x2,
// family x2, sku x1.5, description x1, unit/pack x1, plus a strong
// PREFIX bonus, which is what makes type-ahead feel right: after "coc"
// a customer expects "Coca-Cola" first, and pure token overlap scores
// that zero because "coc" is not a whole token.

export const MAX_QUERY_LENGTH = 64;
export const DEFAULT_SUGGESTION_LIMIT = 8;

const TOKEN_RE = /[^a-z0-9]+/;

export function normalizeQuery(q) {
  if (q === null || q === undefined) return "";
  return String(q).replace(/\s+/g, " ").trim().slice(0, MAX_QUERY_LENGTH);
}

function tokens(value) {
  return String(value || "").toLowerCase().split(TOKEN_RE).filter(Boolean);
}

export function scoreProduct(product, query) {
  const q = normalizeQuery(query).toLowerCase();
  if (!q) return 0;
  const qTokens = q.split(/\s+/).filter(Boolean);

  const name = String(product.name || "").toLowerCase();
  const brand = String(product.brand || "").toLowerCase();
  const family = String(product.product_family || "").toLowerCase();
  const category = String(product.category || "").toLowerCase();
  const description = String(product.description || "").toLowerCase();
  const sku = String(product.sku || "").toLowerCase();
  const packBits = [
    product.unit_of_measure, product.package_size_label,
    product.package_size_value, product.pack_label,
  ].filter((v) => v !== null && v !== undefined && v !== "").join(" ").toLowerCase();
  const tagBits = (product.tags || []).join(" ").toLowerCase();

  const weighted = [
    [tokens(name), 3], [tokens(brand), 3], [tokens(category), 2],
    [tokens(tagBits), 2], [tokens(family), 2], [tokens(sku), 1.5],
    [tokens(description), 1], [tokens(packBits), 1],
  ];

  let score = 0;
  for (const qt of qTokens) {
    for (const [fieldTokens, weight] of weighted) {
      if (fieldTokens.includes(qt)) score += weight;
    }
  }

  // Whole-string prefix — the strongest possible signal.
  if (name.startsWith(q)) score += 6;
  if (brand && brand.startsWith(q)) score += 6;
  if (family && family.startsWith(q)) score += 4;

  // Per-token prefix, so "gate ric" reaches "India Gate Basmati Rice".
  for (const [field, weight] of [[name, 3], [brand, 3], [family, 2]]) {
    if (!field) continue;
    const fieldTokens = field.split(TOKEN_RE).filter(Boolean);
    for (const qt of qTokens) {
      if (fieldTokens.some((ft) => ft.startsWith(qt))) score += weight;
    }
  }

  // Low-weight catch-all so a size, unit or description word breaks a
  // tie rather than dominating.
  const haystack = [name, brand, family, category, description, packBits, tagBits].join(" ");
  for (const qt of qTokens) {
    if (haystack.includes(qt)) score += 0.5;
  }
  return score;
}

// Only products a customer could actually order. Mirrors the backend's
// own `_base_query` eligibility rule exactly.
export function isEligible(product) {
  return product && product.available !== false && product.active !== false;
}

export function rankProducts(products, query, limit = DEFAULT_SUGGESTION_LIMIT) {
  const q = normalizeQuery(query);
  if (!q) return [];
  const scored = [];
  for (const p of products || []) {
    if (!isEligible(p)) continue;
    const score = scoreProduct(p, q);
    if (score > 0) scored.push({ score, name: String(p.name || "").toLowerCase(), product: p });
  }
  // The alphabetical final tiebreak matters: without it two equally
  // scored products could swap places between keystrokes and the list
  // would visibly jitter under the customer's finger.
  scored.sort((a, b) => (b.score - a.score) || a.name.localeCompare(b.name));
  return scored.slice(0, Math.max(1, limit)).map((s) => s.product);
}

// Group suggestions (Brand / Product Family) whose own value matches
// the query — so typing "coca" offers both the individual drinks and
// the whole Coca-Cola brand as one browsable group.
function groupSuggestions(products, query, { key, idKey, type }, max = 2) {
  const needle = normalizeQuery(query).toLowerCase();
  if (!needle) return [];
  const counts = new Map();
  for (const p of products || []) {
    if (!isEligible(p)) continue;
    const value = p[key];
    if (!value || !String(value).toLowerCase().includes(needle)) continue;
    const normalized = String(value).toLowerCase().replace(/\s+/g, " ").trim();
    const entry = counts.get(normalized) || {
      type, value, value_id: p[idKey] || null, value_normalized: normalized, count: 0,
    };
    entry.count += 1;
    counts.set(normalized, entry);
  }
  return [...counts.values()]
    .sort((a, b) => (b.count - a.count) || a.value.localeCompare(b.value))
    .slice(0, max);
}

// The full suggestion list for the small-catalog (client) path. Shape
// matches the backend `/catalog/search` response exactly so `SearchBar`
// renders one list without caring which path produced it.
export function buildSuggestions(products, query, limit = DEFAULT_SUGGESTION_LIMIT) {
  const q = normalizeQuery(query);
  if (!q) return [];
  const out = rankProducts(products, q, limit).map((p) => ({
    type: "product",
    id: p.id,
    label: p.name || "",
    category: p.category || null,
    brand: p.brand || null,
    pack_label: p.pack_label || null,
    price: p.price,
    currency: p.currency || null,
    image_url: p.image_url || null,
    is_vegan: !!p.is_vegan,
    spice_level: p.spice_level || null,
  }));
  out.push(...groupSuggestions(products, q, {
    key: "brand", idKey: "brand_id", type: "brand",
  }));
  out.push(...groupSuggestions(products, q, {
    key: "product_family", idKey: "product_family_id", type: "family",
  }));
  return out;
}

// Filter a category list in place for the "filtered results" view. Keeps
// the category grouping the customer already understands rather than
// flattening the menu into an undifferentiated list.
export function filterCategories(categories, query, facet) {
  const q = normalizeQuery(query);
  const hasFacet = !!(facet && facet.value_normalized);
  if (!q && !hasFacet) return categories;
  const out = [];
  for (const cat of categories || []) {
    // The Favorites tab is a synthetic entry with its own data source —
    // a customer searching for "cake" must not have their Favorites
    // emptied, so it is never filtered.
    if (cat.isFavoritesTab) {
      out.push(cat);
      continue;
    }
    let products = cat.products || [];
    if (hasFacet) {
      const key = facet.type === "brand" ? "brand" : "product_family";
      products = products.filter(
        (p) => String(p[key] || "").toLowerCase().replace(/\s+/g, " ").trim() === facet.value_normalized,
      );
    }
    if (q) {
      const ranked = new Set(rankProducts(products, q, products.length || 1).map((p) => p.id));
      products = products.filter((p) => ranked.has(p.id));
    }
    if (products.length) out.push({ ...cat, products });
  }
  return out;
}
